import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import { RadioDot } from "@/components/RadioOption";

interface QuotaSettings {
  maxWorkflowRunsPerMonth: number;
  autoBumpOnOverage: boolean;
  billingPreference: "manual" | "invoice" | "stripe";
  invoiceTermsAgreed: boolean;
  invoiceTermsAgreedAt: string | null;
  usageWarningThreshold: number;
  usageCriticalThreshold: number;
  usageAlertEmails: string;
  enableUsageAlerts: boolean;
  overageBehavior: string;
}

// Pricing tiers — matches the server-side auto-bump logic exactly.
// 0 = unlimited (Enterprise / site 1032).
const QUOTA_TIERS = [0, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];

function formatQuota(n: number): string {
  if (n === 0) return "Unlimited";
  return n.toLocaleString();
}

function nextTier(current: number): number {
  const above = QUOTA_TIERS.filter(t => t > current);
  return above.length > 0 ? above[0] : 0;
}

function tierLabel(n: number): string {
  if (n === 0) return "Unlimited — Enterprise";
  if (n <= 1000) return `${formatQuota(n)} runs / mo — Starter`;
  if (n <= 5000) return `${formatQuota(n)} runs / mo — Professional`;
  return `${formatQuota(n)} runs / mo — Enterprise`;
}

export default function AutoScalePanel() {
  const api = useApi();
  const [settings, setSettings] = useState<QuotaSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local editable state
  const [autoBump, setAutoBump] = useState(false);
  const [billingPref, setBillingPref] = useState<"manual" | "invoice">("manual");
  const [invoiceAgreed, setInvoiceAgreed] = useState(false);
  const [enableAlerts, setEnableAlerts] = useState(false);
  const [alertEmails, setAlertEmails] = useState("");
  const [warnThreshold, setWarnThreshold] = useState(80);
  const [critThreshold, setCritThreshold] = useState(90);

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/billing/quota-settings");
      if (resp.ok) {
        const data: QuotaSettings = await resp.json();
        setSettings(data);
        setAutoBump(data.autoBumpOnOverage ?? false);
        setBillingPref(data.billingPreference === "invoice" ? "invoice" : "manual");
        setInvoiceAgreed(data.invoiceTermsAgreed ?? false);
        setEnableAlerts(data.enableUsageAlerts ?? false);
        setAlertEmails(data.usageAlertEmails ?? "");
        setWarnThreshold(data.usageWarningThreshold ?? 80);
        setCritThreshold(data.usageCriticalThreshold ?? 90);
        setError(null);
      } else {
        setError("Failed to load quota settings.");
      }
    } catch {
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const resp = await api.messaging.post("admin/billing/quota-settings", {
        autoBumpOnOverage: autoBump,
        billingPreference: billingPref,
        invoiceTermsAgreed: invoiceAgreed,
        enableUsageAlerts: enableAlerts,
        usageAlertEmails: alertEmails,
        usageWarningThreshold: warnThreshold,
        usageCriticalThreshold: critThreshold
      });
      if (resp.ok) {
        setSaved(true);
        await fetchSettings();
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save settings.");
      }
    } catch {
      setError("Failed to connect to server.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="p-6">
        <div className="bg-crimson-red-50 border border-crimson-red-200 rounded-lg p-4 text-sm text-crimson-red-700">
          {error}
          <button onClick={fetchSettings} className="ml-3 text-primary hover:text-primary-active font-medium">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentQuota = settings?.maxWorkflowRunsPerMonth ?? 0;
  const next = nextTier(currentQuota);

  return (
    <div className="p-6 space-y-6">
      {/* Current quota card */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="text-base font-semibold text-slate-gray-800 mb-1">Workflow Run Quota</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Counts every workflow execution — regardless of trigger (conversation, AI tool, scheduled timer, or API call).
          One counter, simple pricing.
        </p>
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold text-primary">{formatQuota(currentQuota)}</span>
          <span className="text-sm text-slate-gray-500">runs / month</span>
        </div>
        {currentQuota > 0 && next > 0 && (
          <p className="text-xs text-slate-gray-400 mt-1">
            Next tier: <span className="font-medium text-slate-gray-600">{tierLabel(next)}</span>
          </p>
        )}
        {currentQuota === 0 && (
          <p className="text-xs text-slate-gray-400 mt-1">You are on the Enterprise / Unlimited plan.</p>
        )}
      </div>

      {/* Auto-Scale settings */}
      {currentQuota > 0 && (
        <div className="bg-white rounded-lg border border-slate-gray-200 p-5 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-slate-gray-800">Auto-Scale on Limit</h3>
            <p className="text-sm text-slate-gray-500 mt-0.5">
              When enabled, your quota automatically upgrades to the next tier the moment you reach your limit —
              so your workflows never get blocked mid-month.
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <div
              role="switch"
              aria-checked={autoBump}
              tabIndex={0}
              onClick={() => setAutoBump(v => !v)}
              onKeyDown={e => { if (e.key === " " || e.key === "Enter") setAutoBump(v => !v); }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer ${
                autoBump ? "bg-primary" : "bg-slate-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  autoBump ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </div>
            <span className="text-sm font-medium text-slate-gray-700">
              Automatically upgrade my quota when I hit my limit
            </span>
          </label>

          {autoBump && (
            <div className="pl-14 space-y-4">
              <p className="text-sm text-slate-gray-600">
                When you reach <strong>{formatQuota(currentQuota)}</strong> runs, your plan will automatically upgrade to{" "}
                <strong>{tierLabel(next)}</strong>.
              </p>

              <div>
                <p className="text-sm font-medium text-slate-gray-700 mb-2">How should we charge for the upgrade?</p>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="radio" name="billingPref" value="invoice" checked={billingPref === "invoice"} onChange={() => setBillingPref("invoice")} className="sr-only" />
                    <RadioDot checked={billingPref === "invoice"} className="mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-slate-gray-700">Invoice me (NET-30)</span>
                      <p className="text-xs text-slate-gray-500 mt-0.5">
                        We'll send you an invoice within 24 hours of the upgrade. Payment due within 30 days.
                        Requires agreement to invoice terms below.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-not-allowed opacity-50">
                    <input type="radio" name="billingPref" value="stripe" disabled className="sr-only" />
                    <RadioDot checked={false} className="mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-slate-gray-700">Auto-charge my card</span>
                      <p className="text-xs text-slate-gray-500 mt-0.5">
                        Instantly charge the card on file.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {billingPref === "invoice" && (
                <div className="bg-slate-gray-50 rounded-lg border border-slate-gray-200 p-4 space-y-3">
                  <p className="text-xs font-semibold text-slate-gray-700 uppercase tracking-wide">Invoice Terms Agreement</p>
                  <p className="text-xs text-slate-gray-600 leading-relaxed">
                    By enabling invoice billing, you agree that Velaro may automatically upgrade your workflow run quota
                    to the next pricing tier when your current quota is reached. An invoice for the prorated amount
                    will be issued within one business day. Payment is due within <strong>30 days</strong> of the invoice date.
                    Continued use of the platform constitutes acceptance of all invoices issued under this agreement.
                    You may disable auto-scale at any time from this settings page.
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invoiceAgreed}
                      onChange={e => setInvoiceAgreed(e.target.checked)}
                      className="mt-0.5"
                      disabled={settings?.invoiceTermsAgreed === true} // Once agreed, can't un-agree
                    />
                    <span className="text-xs text-slate-gray-700">
                      {settings?.invoiceTermsAgreed
                        ? `I agreed to these terms on ${settings.invoiceTermsAgreedAt ? new Date(settings.invoiceTermsAgreedAt).toLocaleDateString() : "record"}.`
                        : "I agree to the invoice billing terms above."}
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Alert thresholds */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-gray-800">Usage Alert Emails</h3>
          <p className="text-sm text-slate-gray-500 mt-0.5">
            Get notified when any monthly metric (workflow runs, SMS, conversations, etc.) approaches your plan limits.
          </p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <div
            role="switch"
            aria-checked={enableAlerts}
            tabIndex={0}
            onClick={() => setEnableAlerts(v => !v)}
            onKeyDown={e => { if (e.key === " " || e.key === "Enter") setEnableAlerts(v => !v); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer ${
              enableAlerts ? "bg-primary" : "bg-slate-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                enableAlerts ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </div>
          <span className="text-sm font-medium text-slate-gray-700">Send usage alert emails</span>
        </label>

        {enableAlerts && (
          <div className="pl-14 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-gray-600 mb-1">
                  Warning threshold (%)
                </label>
                <input
                  type="number"
                  min={1}
                  max={98}
                  value={warnThreshold}
                  onChange={e => setWarnThreshold(Math.min(98, Math.max(1, Number(e.target.value))))}
                  className="w-full border border-slate-gray-200 rounded-lg px-3 py-2 text-sm text-slate-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
                />
                <p className="text-xs text-slate-gray-400 mt-0.5">First email alert (amber)</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-gray-600 mb-1">
                  Critical threshold (%)
                </label>
                <input
                  type="number"
                  min={2}
                  max={99}
                  value={critThreshold}
                  onChange={e => setCritThreshold(Math.min(99, Math.max(2, Number(e.target.value))))}
                  className="w-full border border-slate-gray-200 rounded-lg px-3 py-2 text-sm text-slate-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
                />
                <p className="text-xs text-slate-gray-400 mt-0.5">Second email alert (red)</p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-gray-600 mb-1">
                Alert email addresses (comma-separated — leave blank to auto-send to site admins)
              </label>
              <input
                type="text"
                value={alertEmails}
                onChange={e => setAlertEmails(e.target.value)}
                placeholder="billing@yourcompany.com, admin@yourcompany.com"
                className="w-full border border-slate-gray-200 rounded-lg px-3 py-2 text-sm text-slate-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
              />
            </div>
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || (autoBump && billingPref === "invoice" && !invoiceAgreed)}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
        {saved && (
          <span className="text-sm text-emerald-green-600 font-medium">Settings saved.</span>
        )}
        {error && !saving && (
          <span className="text-sm text-crimson-red-600">{error}</span>
        )}
        {autoBump && billingPref === "invoice" && !invoiceAgreed && (
          <span className="text-xs text-sweet-orange-600">
            Please agree to the invoice terms before saving.
          </span>
        )}
      </div>
    </div>
  );
}
